import type { MetricDefinition, TargetPeriodType } from "@/grid/core/types";
import {
  RECURRING_WEEK_PERIOD_START,
  RECURRING_WEEK_PERIOD_TYPE,
} from "@/grid/core/target-periods";
import type { TeamGroup } from "@/grid/data/assemble-grid";
import {
  isWeekTargetSheet,
  type TargetSheetSlice,
  type WeekTargetSheetValues,
} from "@/targets/build-sheet-values";
import { formatMetricForEditor } from "@/grid/core/format-metric";
import { deleteTarget, upsertTarget } from "@/targets/api/targets-api";
import { isTargetInputInvalid, parseTargetInput } from "@/targets/lib/parse-target-value";
import { cn } from "@/lib/cn";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

type RowKind = "default" | "team";
type SheetSection = "recurring" | "override" | "single";

interface CellCoord {
  section: SheetSection;
  rowKind: RowKind;
  teamKey: string | null;
  metricId: string;
}

interface TargetSpreadsheetProps {
  departmentId: string;
  periodType: TargetPeriodType;
  periodStart: string;
  metrics: MetricDefinition[];
  teams: TeamGroup[];
  sheet: TargetSheetSlice | WeekTargetSheetValues;
  onSaved?: () => void;
  weekSectionFilter?: "both" | "recurring" | "override";
  headerToolbar?: ReactNode;
  minimal?: boolean;
  /** Render inside a parent panel (lighter section chrome). */
  embedded?: boolean;
}

function formatStoredValue(value: number | null, metric: MetricDefinition): string {
  return formatMetricForEditor(value, metric.format);
}

function resolvePeriod(
  section: SheetSection,
  periodType: TargetPeriodType,
  periodStart: string,
): {
  periodType: TargetPeriodType;
  periodStart: string;
} {
  if (section === "recurring") {
    return { periodType: RECURRING_WEEK_PERIOD_TYPE, periodStart: RECURRING_WEEK_PERIOD_START };
  }
  if (section === "override") {
    return { periodType: "week", periodStart };
  }
  return { periodType, periodStart };
}

export function TargetSpreadsheet({
  departmentId,
  periodType,
  periodStart,
  metrics,
  teams,
  sheet,
  onSaved,
  weekSectionFilter = "both",
  headerToolbar,
  minimal = false,
  embedded = false,
}: TargetSpreadsheetProps) {
  const weekSheet = isWeekTargetSheet(sheet) ? sheet : null;

  const metricsById = useMemo(
    () => new Map(metrics.map((metric) => [metric.id, metric])),
    [metrics],
  );

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [inherited, setInherited] = useState<Record<string, boolean>>({});
  const [invalidCells, setInvalidCells] = useState<Record<string, boolean>>({});
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const sliceFor = useCallback(
    (section: SheetSection): TargetSheetSlice => {
      if (weekSheet) {
        return section === "recurring" ? weekSheet.recurring : weekSheet.overrides;
      }
      return sheet as TargetSheetSlice;
    },
    [sheet, weekSheet],
  );

  const recurringSlice = weekSheet?.recurring;

  const storedValue = useCallback(
    (section: SheetSection, coord: Omit<CellCoord, "section">): number | null => {
      const slice = sliceFor(section);
      if (coord.rowKind === "default") return slice.defaults[coord.metricId] ?? null;
      return slice.teams[coord.teamKey!]?.[coord.metricId] ?? null;
    },
    [sliceFor],
  );

  const inheritedValue = useCallback(
    (coord: Omit<CellCoord, "section">): number | null => {
      if (!recurringSlice) return null;
      if (coord.rowKind === "default") return recurringSlice.defaults[coord.metricId] ?? null;
      return recurringSlice.teams[coord.teamKey!]?.[coord.metricId] ?? null;
    },
    [recurringSlice],
  );

  const cells = useMemo((): CellCoord[] => {
    let sections: SheetSection[];
    if (weekSheet) {
      if (weekSectionFilter === "recurring") {
        sections = ["recurring"];
      } else if (weekSectionFilter === "override") {
        sections = ["override"];
      } else {
        sections = ["recurring", "override"];
      }
    } else {
      sections = ["single"];
    }
    const rows: CellCoord[] = [];

    for (const section of sections) {
      for (const metric of metrics) {
        rows.push({ section, rowKind: "default", teamKey: null, metricId: metric.id });
      }
      for (const team of teams) {
        for (const metric of metrics) {
          rows.push({ section, rowKind: "team", teamKey: team.groupKey, metricId: metric.id });
        }
      }
    }

    return rows;
  }, [metrics, teams, weekSheet, weekSectionFilter]);

  const cellKey = (coord: CellCoord): string => {
    const teamPart = coord.rowKind === "default" ? "default" : coord.teamKey!;
    return `${coord.section}:${teamPart}:${coord.metricId}`;
  };

  useEffect(() => {
    const nextDraft: Record<string, string> = {};
    const nextInherited: Record<string, boolean> = {};

    for (const coord of cells) {
      const key = cellKey(coord);
      const metric = metricsById.get(coord.metricId);
      if (!metric) continue;

      const explicit = storedValue(coord.section, coord);

      if (coord.section === "override" && explicit == null) {
        const fallback = inheritedValue(coord);
        if (fallback != null) {
          nextDraft[key] = formatStoredValue(fallback, metric);
          nextInherited[key] = true;
          continue;
        }
      }

      nextDraft[key] = formatStoredValue(explicit, metric);
      nextInherited[key] = false;
    }

    setDraft(nextDraft);
    setInherited(nextInherited);
    setInvalidCells({});
  }, [cells, inheritedValue, metricsById, storedValue]);

  const saveCell = useCallback(
    async (coord: CellCoord, raw: string) => {
      const metric = metrics.find((m) => m.id === coord.metricId);
      if (!metric) return;

      const key = cellKey(coord);
      const trimmed = raw.trim();

      if (isTargetInputInvalid(raw)) {
        setInvalidCells((prev) => ({ ...prev, [key]: true }));
        return;
      }

      setInvalidCells((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });

      const parsed = parseTargetInput(raw);
      const scopeType = coord.rowKind === "default" ? "department" : "team_group";
      const scopeKey = coord.rowKind === "default" ? departmentId : coord.teamKey!;
      const period = resolvePeriod(coord.section, periodType, periodStart);
      const explicit = storedValue(coord.section, coord);
      const recurring = coord.section === "override" ? inheritedValue(coord) : null;

      if (coord.section === "override") {
        if (parsed == null && explicit == null) return;
        if (parsed != null && explicit == null && recurring != null && parsed === recurring) {
          setDraft((prev) => ({ ...prev, [key]: formatStoredValue(parsed, metric) }));
          return;
        }
      }

      if (parsed === explicit) {
        if (parsed != null && trimmed !== formatStoredValue(parsed, metric)) {
          setDraft((prev) => ({ ...prev, [key]: formatStoredValue(parsed, metric) }));
        }
        return;
      }

      try {
        if (parsed == null) {
          await deleteTarget({
            scopeType,
            scopeKey,
            metric: metric.targetMetric,
            periodType: period.periodType,
            periodStart: period.periodStart,
          });
        } else {
          await upsertTarget({
            scopeType,
            scopeKey,
            metric: metric.targetMetric,
            periodType: period.periodType,
            periodStart: period.periodStart,
            targetValue: parsed,
          });
        }
        if (parsed != null) {
          setDraft((prev) => ({ ...prev, [key]: formatStoredValue(parsed, metric) }));
        }
        onSaved?.();
      } catch {
        void 0;
      }
    },
    [departmentId, metrics, onSaved, periodStart, periodType, storedValue, inheritedValue],
  );

  const focusAt = (index: number) => {
    const el = inputRefs.current[index];
    if (el) {
      el.focus();
      el.select();
    }
  };

  const handleKeyDown = (
    index: number,
    coord: CellCoord,
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Tab") {
      event.preventDefault();
      const next = event.shiftKey ? index - 1 : index + 1;
      if (next >= 0 && next < cells.length) focusAt(next);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      void saveCell(coord, event.currentTarget.value);
      const next = index + metrics.length;
      if (next < cells.length) focusAt(next);
    }
  };

  const handleFocus = (key: string) => {
    setInherited((prev) => (prev[key] ? { ...prev, [key]: false } : prev));
  };

  let inputIndex = 0;

  const renderSection = (section: SheetSection, title: string, subtitle?: string) => {
    const showOverrideSpacing = section === "override" && weekSectionFilter === "both";
    const toolbarSection = section === "override" || section === "single";
    const showHeader =
      !minimal || section === "recurring" || (minimal && headerToolbar != null && toolbarSection);
    const headerIsToolbarOnly = minimal && headerToolbar != null && toolbarSection;

    return (
      <div
        key={section}
        className={cn(
          embedded
            ? "overflow-hidden rounded-2xl ring-1 ring-[var(--color-border)]"
            : "rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4",
          embedded && "p-4",
          showOverrideSpacing && "mt-6",
        )}
      >
        {showHeader ? (
          <div className={cn("mb-2", headerIsToolbarOnly && "mb-3")}>
            {headerIsToolbarOnly ? (
              headerToolbar
            ) : (
              <>
                <h2 className="text-sm font-medium text-[var(--color-text)]">{title}</h2>
                {!minimal && subtitle ? (
                  <p className="text-xs text-[var(--color-text-muted)]">{subtitle}</p>
                ) : null}
                {section === "override" && headerToolbar ? (
                  <div className="mt-3">{headerToolbar}</div>
                ) : null}
              </>
            )}
          </div>
        ) : null}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead className="bg-[var(--color-surface-muted)]">
              <tr className="border-b border-[var(--color-border)]">
                <th className="sticky left-0 z-10 bg-[var(--color-surface-muted)] px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
                  Team
                </th>
                {metrics.map((metric) => (
                  <th
                    key={metric.id}
                    className="px-2 py-2 text-center text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]"
                  >
                    {metric.shortLabel ?? metric.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface-muted)]/80">
                <td className="sticky left-0 z-10 bg-[var(--color-surface-muted)] px-3 py-1.5 text-xs font-medium text-[var(--color-text-muted)]">
                  All teams
                </td>
                {metrics.map((metric) => {
                  const coord: CellCoord = {
                    section,
                    rowKind: "default",
                    teamKey: null,
                    metricId: metric.id,
                  };
                  const key = cellKey(coord);
                  const idx = inputIndex++;
                  return (
                    <td key={metric.id} className="px-1 py-1">
                      {renderInput(idx, coord, key)}
                    </td>
                  );
                })}
              </tr>

              {teams.map((team) => (
                <tr
                  key={team.groupKey}
                  className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface-muted)]/50"
                >
                  <td className="sticky left-0 z-10 bg-white px-3 py-1.5 font-medium text-[var(--color-text)]">
                    {team.name}
                  </td>
                  {metrics.map((metric) => {
                    const coord: CellCoord = {
                      section,
                      rowKind: "team",
                      teamKey: team.groupKey,
                      metricId: metric.id,
                    };
                    const key = cellKey(coord);
                    const idx = inputIndex++;
                    return (
                      <td key={metric.id} className="px-1 py-1">
                        {renderInput(idx, coord, key)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  function renderInput(index: number, coord: CellCoord, key: string) {
    const isInherited = inherited[key] ?? false;
    const isInvalid = invalidCells[key] ?? false;
    const metric = metricsById.get(coord.metricId);
    const isCurrency = metric?.format === "currency";

    return (
      <input
        ref={(el) => {
          inputRefs.current[index] = el;
        }}
        type="text"
        inputMode="decimal"
        value={draft[key] ?? ""}
        onChange={(event) => {
          setDraft((prev) => ({ ...prev, [key]: event.target.value }));
          if (isInherited) {
            setInherited((prev) => ({ ...prev, [key]: false }));
          }
          if (isInvalid) {
            setInvalidCells((prev) => {
              const next = { ...prev };
              delete next[key];
              return next;
            });
          }
        }}
        onFocus={() => handleFocus(key)}
        onBlur={(event) => void saveCell(coord, event.target.value)}
        onKeyDown={(event) => handleKeyDown(index, coord, event)}
        className={cn(
          "h-9 w-full rounded-lg border border-transparent bg-white px-2 text-center tabular-nums text-[var(--color-text)]",
          "focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/15",
          isCurrency && "min-w-[5.5rem]",
          isInherited &&
            "italic text-[var(--color-text-muted)] placeholder:text-[var(--color-text-muted)]",
          isInvalid && "border-[var(--color-danger)] text-[var(--color-danger)]",
        )}
        aria-label={`${coord.rowKind === "default" ? "All teams" : coord.teamKey} ${coord.metricId} target`}
      />
    );
  }

  inputIndex = 0;

  return (
    <div>
      {weekSheet ? (
        <>
          {weekSectionFilter === "both" || weekSectionFilter === "recurring"
            ? renderSection(
                "recurring",
                "Every week",
                minimal ? undefined : "Set once — applies to all weeks unless overridden below.",
              )
            : null}
          {weekSectionFilter === "both" || weekSectionFilter === "override"
            ? renderSection(
                "override",
                "This week only",
                minimal
                  ? undefined
                  : weekSectionFilter === "override"
                    ? "One-off overrides for a specific week. Inherited values shown in gray italics."
                    : `Overrides for week of ${periodStart}. Inherited values shown in gray italics.`,
              )
            : null}
        </>
      ) : (
        renderSection("single", "Targets")
      )}
    </div>
  );
}
